import { useContext, useEffect, useRef } from "react";

import {
  type ContextNode,
  ContextTreeContext,
  HotkeyScope,
  countDescendantDepth,
  findNearestContextNode,
  findResetHotkeysBoundary,
  isAncestorOrSelf,
} from "./useContextTree";

// -------------------------
// Types
// -------------------------

export type HotkeyCallback = (event: KeyboardEvent) => void;
export type ScopeCheckFn = (
  handlerNode: ContextNode | null,
  activeNode: ContextNode | null,
) => boolean;
export type OS = "mac" | "windows" | "linux" | "unknown";
export type IsEnabledFn = () => boolean;

export interface HotkeyRegistration {
  id: string;
  combo: string[];
  comboKey: string;
  callback: HotkeyCallback;
  contextNode: ContextNode | null;
  scopeCheck?: ScopeCheckFn;
  isEnabled?: boolean | (() => boolean);
}

export type HotkeyComboInput =
  | string
  | string[]
  | Partial<Record<OS, string>>
  | Partial<Record<OS, string[]>>;

export interface HotkeyConfig {
  id: string;
  combos: HotkeyComboInput;
  callback: HotkeyCallback;
  scopeCheck?: ScopeCheckFn;
  isEnabled?: boolean | IsEnabledFn;
}

// -------------------------
// Module state
// -------------------------

let isListenerAttached = false;
export let registrationCounter = 0;
export function nextRegistrationId(prefix: string): string {
  return `${prefix}-${++registrationCounter}`;
}

const hotkeysByCombo = new Map<string, Set<HotkeyRegistration>>();
const hotkeyById = new Map<string, HotkeyRegistration>();

let capturedActiveNode: ContextNode | null = null;
let capturedActiveElement: Element | null = null;

// When a third-party overlay (e.g. Plaid Link iframe) owns the keyboard,
// suspend app hotkeys so Escape / single-letter shortcuts don't steal
// keystrokes or yank focus back into the Command Center dialog.
let hotkeysSuspended = false;

export function setHotkeysSuspended(suspended: boolean) {
  hotkeysSuspended = suspended;
}

export function areHotkeysSuspended() {
  return hotkeysSuspended;
}

// -------------------------
// Cached values
// -------------------------

let cachedOS: OS | null = null;
export function getOS(): OS {
  if (cachedOS !== null) return cachedOS;
  const platform = navigator.platform.toLowerCase();
  if (platform.includes("mac")) cachedOS = "mac";
  else if (platform.includes("win")) cachedOS = "windows";
  else if (platform.includes("linux")) cachedOS = "linux";
  else cachedOS = "unknown";
  return cachedOS;
}

const normalizeComboCache = new Map<string, string[]>();

export function normalizeCombo(combo: string): string[] {
  const cached = normalizeComboCache.get(combo);
  if (cached) return cached;

  const result = combo
    .toLowerCase()
    .split("+")
    .map((k) => {
      const trimmed = k.trim();
      if (trimmed === "space") return " ";
      if (trimmed === "cmd" || trimmed === "command") return "meta";
      return trimmed;
    })
    .filter((k) => k.length > 0)
    .sort();

  normalizeComboCache.set(combo, result);
  return result;
}

// Pre-computed escape combo
const ESCAPE_COMBO = normalizeCombo("escape");
const ESCAPE_COMBO_KEY = ESCAPE_COMBO.join("+");

export { ESCAPE_COMBO, ESCAPE_COMBO_KEY };

export function comboToKey(combo: string[]): string {
  return combo.join("+");
}

export function resolveCombos(combos: HotkeyComboInput, os: OS): string[] {
  if (typeof combos === "string") return [combos];
  if (Array.isArray(combos)) return combos;
  const osValue = combos[os] || combos.windows || combos.linux || combos.mac;
  if (!osValue) return [];
  return Array.isArray(osValue) ? osValue : [osValue];
}

// -------------------------
// Registry
// -------------------------

export function registerHotkey(registration: HotkeyRegistration) {
  hotkeyById.set(registration.id, registration);
  const existing = hotkeysByCombo.get(registration.comboKey);
  if (existing) {
    existing.add(registration);
  } else {
    hotkeysByCombo.set(registration.comboKey, new Set([registration]));
  }
}

export function unregisterHotkey(id: string) {
  const registration = hotkeyById.get(id);
  if (!registration) return;
  hotkeyById.delete(id);
  const comboSet = hotkeysByCombo.get(registration.comboKey);
  if (comboSet) {
    comboSet.delete(registration);
    if (comboSet.size === 0) {
      hotkeysByCombo.delete(registration.comboKey);
    }
  }
}

// -------------------------
// Helpers
// -------------------------

const MODIFIER_KEYS: ReadonlySet<string> = new Set([
  "meta",
  "control",
  "ctrl",
  "alt",
  "shift",
  "altgraph",
]);

const CURSOR_KEYS: ReadonlySet<string> = new Set([
  "arrowleft",
  "arrowright",
  "arrowup",
  "arrowdown",
  "home",
  "end",
]);

function getComboKey(event: KeyboardEvent): string {
  const parts: string[] = [];
  if (event.metaKey) parts.push("meta");
  if (event.ctrlKey) parts.push("ctrl");
  if (event.altKey) parts.push("alt");
  if (event.shiftKey) parts.push("shift");

  const key = event.key.toLowerCase();
  if (key && !MODIFIER_KEYS.has(key)) parts.push(key);

  if (parts.length <= 1) return parts[0] ?? "";
  parts.sort();
  return parts.join("+");
}

function checkIsEnabled(
  isEnabled: boolean | (() => boolean) | undefined,
): boolean {
  if (isEnabled === undefined) return true;
  if (typeof isEnabled === "boolean") return isEnabled;
  return isEnabled();
}

export function getContextDistance(
  activeNode: ContextNode | null,
  targetNode: ContextNode | null,
): number {
  if (!targetNode) return Infinity;
  return countDescendantDepth(targetNode, activeNode);
}

// -------------------------
// Global keyboard listener
// -------------------------

export function attachGlobalKeyboardListener() {
  if (isListenerAttached) return;
  isListenerAttached = true;

  document.addEventListener(
    "keydown",
    () => {
      capturedActiveNode = findNearestContextNode(document.activeElement);
      capturedActiveElement = document.activeElement;
    },
    true,
  );

  window.addEventListener("keydown", (e) => {
    if (e.defaultPrevented) return;
    if (hotkeysSuspended) return;
    // Enter does what it does once, held or not: the browser's repeats of a
    // held key are not presses. A dialog's Book that closes it would otherwise
    // book what opened under it next. Arrows held still step on.
    if (e.repeat && e.key === "Enter") return;

    const focused = document.activeElement as HTMLElement | null;
    if (focused?.tagName === "IFRAME") return;

    if (!e.metaKey && !e.ctrlKey && !e.altKey) {
      if (CURSOR_KEYS.has(e.key.toLowerCase())) {
        const el = capturedActiveElement;
        if (el) {
          const tag = (el as Element).tagName;
          if (tag === "TEXTAREA" || (el as HTMLElement).isContentEditable)
            return;
        }
      }
    }

    const comboKey = getComboKey(e);
    if (!comboKey) return;

    const matchingHandlers = hotkeysByCombo.get(comboKey);
    if (!matchingHandlers || matchingHandlers.size === 0) return;

    const activeNode = capturedActiveNode;

    let capturingNode: ContextNode | null = null;
    const hasModifier = e.metaKey || e.ctrlKey || e.altKey;
    const isSinglePrintableKey = !hasModifier && e.key.length === 1;
    if (isSinglePrintableKey) {
      let current: ContextNode | null = activeNode;
      while (current) {
        if (current.capturesInput) {
          capturingNode = current;
          break;
        }
        current = current.parent;
      }

      if (!capturingNode) {
        const el = capturedActiveElement;
        if (el) {
          const tag = (el as Element).tagName;
          const isNativeInput =
            tag === "INPUT" ||
            tag === "TEXTAREA" ||
            tag === "SELECT" ||
            (el as HTMLElement).isContentEditable;
          if (isNativeInput) {
            capturingNode = activeNode;
          }
        }
      }
    }

    const resetBoundary = findResetHotkeysBoundary(activeNode);

    let bestHandler: HotkeyRegistration | null = null;
    let bestDistance = Infinity;

    for (const handler of matchingHandlers) {
      if (!checkIsEnabled(handler.isEnabled)) continue;

      if (resetBoundary) {
        const isGlobal = handler.scopeCheck === HotkeyScope.global;
        if (isGlobal) {
          // Globals pass through plain ``resetHotkeys``. With
          // ``suppressGlobalHotkeys``, only block globals registered *outside*
          // the boundary — handlers inside the overlay (e.g. Command Center
          // topbar) must still fire.
          if (
            resetBoundary.suppressGlobalHotkeys &&
            (!handler.contextNode ||
              !isAncestorOrSelf(resetBoundary, handler.contextNode))
          ) {
            continue;
          }
        } else {
          if (!handler.contextNode) continue;
          if (!isAncestorOrSelf(resetBoundary, handler.contextNode)) continue;
        }
      }

      if (capturingNode) {
        if (!handler.contextNode) continue;
        if (!isAncestorOrSelf(capturingNode, handler.contextNode)) continue;
      }

      const distance = getContextDistance(activeNode, handler.contextNode);

      const check = handler.scopeCheck || HotkeyScope.within;
      let candidateDistance: number;
      if (check === HotkeyScope.within) {
        if (distance < 0) {
          // Portaled overlays: focus may remain on the anchor outside the
          // floating DOM while the overlay is open. Hotkeys registered on
          // the reset boundary (or its descendants) still apply when focus
          // sits anywhere in the owning ancestor chain.
          if (
            resetBoundary &&
            handler.contextNode &&
            isAncestorOrSelf(resetBoundary, handler.contextNode) &&
            isAncestorOrSelf(activeNode, resetBoundary)
          ) {
            candidateDistance =
              handler.contextNode === resetBoundary
                ? 0
                : countDescendantDepth(handler.contextNode, activeNode);
            if (candidateDistance < 0) continue;
          } else {
            continue;
          }
        } else {
          candidateDistance = distance;
        }
      } else if (check === HotkeyScope.global) {
        candidateDistance = Infinity;
      } else if (check === HotkeyScope.nearest) {
        if (distance !== 0) continue;
        candidateDistance = 0;
      } else {
        if (!check(handler.contextNode, activeNode)) continue;
        candidateDistance = distance >= 0 ? distance : Infinity;
      }

      if (bestHandler === null || candidateDistance < bestDistance) {
        bestHandler = handler;
        bestDistance = candidateDistance;
        if (candidateDistance === 0) break;
      }
    }

    if (!bestHandler) return;

    e.preventDefault();
    e.stopPropagation();
    bestHandler.callback(e);
  });
}

// -------------------------
// useContextHotkeys
// -------------------------

interface UseContextHotkeysOptions {
  id: string;
  combos: HotkeyComboInput;
  callback: HotkeyCallback;
  scopeCheck?: ScopeCheckFn;
  /** Controls whether this hotkey is registered at all (e.g. feature flags,
   *  conditional rendering). Orthogonal to scopeCheck, which controls spatial
   *  scoping based on current focus position. */
  isEnabled?: boolean | IsEnabledFn;
  contextNode?: ContextNode | null;
}

export function useContextHotkeys({
  id,
  combos,
  callback,
  scopeCheck,
  isEnabled = true,
  contextNode: explicitContextNode,
}: UseContextHotkeysOptions) {
  const contextFromReact = useContext(ContextTreeContext);
  const contextNode =
    explicitContextNode !== undefined ? explicitContextNode : contextFromReact;
  const registrationIdsRef = useRef<string[]>([]);

  useEffect(() => {
    const selectedCombos = resolveCombos(combos, getOS());
    if (selectedCombos.length === 0) return;

    const normalizedCombos = selectedCombos.map(normalizeCombo);

    registrationIdsRef.current.forEach(unregisterHotkey);
    registrationIdsRef.current = [];

    const effectiveScopeCheck: ScopeCheckFn | undefined = scopeCheck
      ? scopeCheck
      : contextNode
        ? HotkeyScope.within
        : undefined;

    const newIds: string[] = [];
    normalizedCombos.forEach((combo) => {
      const registrationId = nextRegistrationId(id);
      const comboKey = comboToKey(combo);
      registerHotkey({
        id: registrationId,
        combo,
        comboKey,
        callback,
        contextNode,
        scopeCheck: effectiveScopeCheck,
        isEnabled,
      });
      newIds.push(registrationId);
    });

    registrationIdsRef.current = newIds;
    attachGlobalKeyboardListener();

    return () => {
      registrationIdsRef.current.forEach(unregisterHotkey);
      registrationIdsRef.current = [];
    };
  }, [id, combos, callback, contextNode, scopeCheck, isEnabled]);
}
